import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 棋类博弈 - 横屏「国际象棋策略」风格
 *
 * 核心亮点：
 * - SVG 8x8 棋盘（深浅格交替）
 * - Unicode 棋子符号动画放置
 * - 走子箭头标注
 * - 策略分析卡片
 * - 局面评估条
 *
 * 尺寸：1920x1080 横屏
 */

interface ChessPiece {
  symbol: string;
  position: [number, number]; // col, row (0-7)
  color: 'white' | 'black';
}

interface Move {
  notation: string;
  explanation: string;
}

interface StrategyCard {
  title: string;
  description: string;
  icon: string;
}

interface ChessStrategyTemplateProps {
  topic: string;
  subtitle: string;
  pieces: ChessPiece[];
  moves: Move[];
  strategy: StrategyCard[];
  evaluation: number;
  keyFact: string;
}

const DEFAULT_PROPS: ChessStrategyTemplateProps = {
  topic: '国际象棋·西西里防御',
  subtitle: 'Sicilian Defense — The Most Popular Response to 1.e4',
  pieces: [
    { symbol: '♔', position: [4, 7], color: 'white' },
    { symbol: '♕', position: [3, 7], color: 'white' },
    { symbol: '♖', position: [0, 7], color: 'white' },
    { symbol: '♖', position: [7, 7], color: 'white' },
    { symbol: '♗', position: [2, 7], color: 'white' },
    { symbol: '♗', position: [5, 7], color: 'white' },
    { symbol: '♘', position: [1, 7], color: 'white' },
    { symbol: '♘', position: [6, 7], color: 'white' },
    { symbol: '♙', position: [4, 6], color: 'white' },
    { symbol: '♙', position: [3, 6], color: 'white' },
    { symbol: '♙', position: [5, 6], color: 'white' },
    { symbol: '♙', position: [2, 5], color: 'white' },
    { symbol: '♚', position: [4, 0], color: 'black' },
    { symbol: '♛', position: [3, 0], color: 'black' },
    { symbol: '♜', position: [0, 0], color: 'black' },
    { symbol: '♜', position: [7, 0], color: 'black' },
    { symbol: '♝', position: [2, 0], color: 'black' },
    { symbol: '♝', position: [5, 0], color: 'black' },
    { symbol: '♞', position: [1, 0], color: 'black' },
    { symbol: '♞', position: [6, 0], color: 'black' },
    { symbol: '♟', position: [5, 1], color: 'black' },
    { symbol: '♟', position: [4, 1], color: 'black' },
    { symbol: '♟', position: [3, 1], color: 'black' },
    { symbol: '♟', position: [2, 2], color: 'black' },
  ],
  moves: [
    { notation: '1. e4', explanation: '白方挺进王前兵，控制中心' },
    { notation: '1... c5', explanation: '西西里防御，黑方反击中心' },
    { notation: '2. Nf3', explanation: '白方出马，准备 d4' },
    { notation: '2... d6', explanation: '黑方巩固 c5 兵' },
  ],
  strategy: [
    { title: '控制中心', description: '西西里防御通过 c5 挑战白方 e4 兵，争夺中心控制权', icon: '🎯' },
    { title: '不对称局面', description: '双方兵型不同，导致复杂战术对抗', icon: '⚔️' },
    { title: '王翼进攻', description: '白方常选择王翼进攻，黑方则寻求后翼反击', icon: '♔' },
  ],
  evaluation: 0.3,
  keyFact: '西西里防御是 1.e4 后最常见的应对，在职业对局中出现率超过 25%，由意大利棋手在 16 世纪发展',
};

export const ChessStrategyTemplate: React.FC<Partial<ChessStrategyTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });

  const boardSize = 520;
  const cellSize = boardSize / 8;
  const boardX = 80;
  const boardY = 180;

  const boardAppear = interpolate(frame, [15, 55], [0, 1], { extrapolateRight: 'clamp' });
  const pieceStart = 40;
  const pieceInterval = 3;

  const moveStart = 80;
  const strategyStart = 140;
  const evalStart = 200;
  const factStart = 260;
  const factOpacity = interpolate(frame, [factStart, factStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Evaluation bar: -1 to +1 maps to 0-100%
  const evalPercent = 50 + (p.evaluation * 50);
  const evalBarWidth = interpolate(frame, [evalStart, evalStart + 30], [0, evalPercent], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: '#1a1a1a',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{ position: 'absolute', top: 28, left: 50, right: 50, opacity: headerProgress, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ padding: '6px 18px', background: '#b5886318', border: '1px solid #d4af3740', borderRadius: 10, fontSize: 22, color: '#d4af37', fontWeight: 700, whiteSpace: 'nowrap' }}>
          ♟ 棋类博弈
        </div>
        <h1 style={{ fontSize: 40, fontWeight: 900, margin: 0, color: '#f0d9b5', whiteSpace: 'nowrap' }}>{p.topic}</h1>
        <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', margin: 0, whiteSpace: 'nowrap' }}>{p.subtitle}</p>
      </div>

      {/* ========== CHESSBOARD ========== */}
      <svg style={{ position: 'absolute', left: boardX, top: boardY }} width={boardSize + 40} height={boardSize + 40} opacity={boardAppear}>
        <defs>
          <linearGradient id="chessLight" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#f0d9b5" />
            <stop offset="100%" stopColor="#e8d4a8" />
          </linearGradient>
          <linearGradient id="chessDark" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#b58863" />
            <stop offset="100%" stopColor="#a67c52" />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width={boardSize + 40} height={boardSize + 40} fill="#1a1a1a" stroke="#d4af37" strokeWidth="2" rx="8" />
        <g transform="translate(20, 20)">
          {Array.from({ length: 8 }).map((_, row) =>
            Array.from({ length: 8 }).map((_, col) => {
              const isLight = (row + col) % 2 === 0;
              return (
                <rect
                  key={`${row}-${col}`}
                  x={col * cellSize}
                  y={row * cellSize}
                  width={cellSize}
                  height={cellSize}
                  fill={isLight ? 'url(#chessLight)' : 'url(#chessDark)'}
                />
              );
            })
          )}
          {/* File labels a-h */}
          {Array.from({ length: 8 }).map((_, i) => (
            <text key={`f${i}`} x={i * cellSize + cellSize / 2} y={boardSize + 18} fill="#d4af37" fontSize="14" textAnchor="middle" fontWeight="600">
              {String.fromCharCode(97 + i)}
            </text>
          ))}
          {/* Rank labels 1-8 (1 at bottom = white's side) */}
          {Array.from({ length: 8 }).map((_, i) => (
            <text key={`r${i}`} x={-8} y={(7 - i) * cellSize + cellSize / 2 + 5} fill="#d4af37" fontSize="14" textAnchor="middle" fontWeight="600">
              {i + 1}
            </text>
          ))}
        </g>
      </svg>

      {/* Chess pieces */}
      {p.pieces.map((piece, i) => {
        const [col, row] = piece.position;
        const cx = boardX + 20 + col * cellSize + cellSize / 2;
        const cy = boardY + 20 + row * cellSize + cellSize / 2;
        const start = pieceStart + i * pieceInterval;
        const progress = spring({ frame: frame - start, fps, from: 0, to: 1, config: { damping: 14, stiffness: 120 } });
        const scale = 0.5 + 0.5 * progress;
        const opacity = interpolate(frame, [start, start + 15], [0, 1], { extrapolateRight: 'clamp' });
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: cx - 24,
              top: cy - 28,
              width: 48,
              height: 56,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 42,
              fontWeight: 900,
              color: piece.color === 'white' ? '#fff' : '#1a1a1a',
              textShadow: piece.color === 'white' ? '1px 1px 2px rgba(0,0,0,0.5)' : '1px 1px 2px rgba(255,255,255,0.3)',
              opacity: boardAppear * opacity,
              transform: `scale(${scale})`,
            }}
          >
            {piece.symbol}
          </div>
        );
      })}

      {/* Move arrow (e4 to c5 example - simplified arrow) */}
      <svg style={{ position: 'absolute', left: boardX + 20, top: boardY + 20 }} width={boardSize} height={boardSize} opacity={interpolate(frame, [100, 130], [0, 0.8], { extrapolateRight: 'clamp' })}>
        <defs>
          <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
            <polygon points="0 0, 10 3.5, 0 7" fill="#d4af37" />
          </marker>
        </defs>
        <line
          x1={4 * cellSize + cellSize / 2}
          y1={4 * cellSize + cellSize / 2}
          x2={2 * cellSize + cellSize / 2}
          y2={3 * cellSize + cellSize / 2}
          stroke="#d4af37"
          strokeWidth="4"
          strokeLinecap="round"
          markerEnd="url(#arrowhead)"
          opacity="0.9"
        />
      </svg>

      {/* ========== RIGHT PANEL ========== */}
      <div style={{ position: 'absolute', top: 160, left: 680, right: 50 }}>
        {/* Moves */}
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>走子记录</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 20px', marginBottom: 24 }}>
          {p.moves.map((m, i) => {
            const opacity = interpolate(frame, [moveStart + i * 18, moveStart + i * 18 + 15], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', opacity }}>
                <span style={{ padding: '6px 14px', background: '#b5886340', border: '1px solid #d4af3750', borderRadius: 8, fontSize: 24, fontWeight: 800, color: '#d4af37' }}>
                  {m.notation}
                </span>
                <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.6)' }}>{m.explanation}</span>
              </div>
            );
          })}
        </div>

        {/* Strategy cards */}
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>策略分析</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {p.strategy.map((s, i) => {
            const progress = spring({ frame: frame - strategyStart - i * 25, fps, from: 0, to: 1, config: { damping: 12, stiffness: 140 } });
            return (
              <div
                key={i}
                style={{
                  padding: '18px 22px',
                  background: 'rgba(181,136,99,0.08)',
                  border: '1px solid rgba(212,175,55,0.25)',
                  borderRadius: 14,
                  opacity: progress,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                  <span style={{ fontSize: 26 }}>{s.icon}</span>
                  <span style={{ fontSize: 24, fontWeight: 800, color: '#d4af37' }}>{s.title}</span>
                </div>
                <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{s.description}</div>
              </div>
            );
          })}
        </div>

        {/* Evaluation bar */}
        <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.4)', marginBottom: 10, letterSpacing: '0.1em', fontWeight: 600 }}>局面评估</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1, height: 24, background: 'rgba(0,0,0,0.4)', borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(212,175,55,0.3)' }}>
              <div
                style={{
                  width: `${Math.min(100, Math.max(0, evalBarWidth))}%`,
                  height: '100%',
                  background: p.evaluation >= 0 ? 'linear-gradient(90deg, #b58863, #d4af37)' : 'linear-gradient(90deg, #8b4513, #a67c52)',
                  borderRadius: 11,
                  transition: 'width 0.3s',
                }}
              />
            </div>
            <span style={{ fontSize: 24, fontWeight: 800, color: p.evaluation >= 0 ? '#d4af37' : '#b58863', minWidth: 60 }}>
              {p.evaluation >= 0 ? '+' : ''}{p.evaluation.toFixed(1)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>
            <span>黑优</span>
            <span>均势</span>
            <span>白优</span>
          </div>
        </div>
      </div>

      {/* Key fact */}
      <div style={{ position: 'absolute', bottom: 44, left: 50, right: 50, opacity: factOpacity }}>
        <div style={{ padding: '18px 28px', background: 'rgba(181,136,99,0.12)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: 16, textAlign: 'center' }}>
          <span style={{ fontSize: 22, color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>💡 {p.keyFact}</span>
        </div>
      </div>

      {/* Progress */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`, height: 3, background: 'linear-gradient(90deg, #b58863, #d4af37)' }} />
    </AbsoluteFill>
  );
};
